import { useEffect, useMemo, useRef, useState } from "react";
import type { Subtitle } from "./subtitle-editor";
import type { CaptionCustomStyle } from "./video-editor";
import { DynamicOverlayPreview } from "../../lib/remotion-preview-runtime";

interface OverlayElement {
  type: string;
  startTime: number;
  endTime: number;
  payload: Record<string, unknown>;
}

interface VideoPreviewOverlayProps {
  currentTime: number;
  subtitles: Subtitle[];
  overlays: OverlayElement[];
  captionStyle: string;
  captionCustom?: CaptionCustomStyle;
  onOverlayClick?: (overlayIndex: number) => void;
  onCustomOverlayOffsetChange?: (overlayIndex: number, nextOffsetY: number) => void;
  showSubtitles?: boolean;
}

// --- Active element finders ---

function findActiveSubtitle(subtitles: Subtitle[], time: number): Subtitle | null {
  return subtitles.find((s) => time >= s.startTime && time < s.endTime) ?? null;
}

function findActiveOverlays(overlays: OverlayElement[], time: number): OverlayElement[] {
  return overlays.filter((o) => time >= o.startTime && time < o.endTime);
}

// --- Safe payload accessor ---

function str(val: unknown, fallback = ""): string {
  if (typeof val === "string") return val;
  if (typeof val === "number") return String(val);
  return fallback;
}

// --- Caption style map ---

const FULLSCREEN_OVERLAY_TYPES = new Set([
  "title-slide",
  "circle-portrait",
  "comparison-rows",
  "gradient-reveal",
]);
const BOTTOM_ZONE_OVERLAY_TYPES = new Set([
  "stat-counter",
  "note-card",
  "comment-box",
  "retention-chart",
  "progress-bar",
  "bullet-list",
]);

const CAPTION_CONFIGS: Record<string, React.CSSProperties> = {
  karaoke: {
    fontWeight: 900,
    fontSize: "1.15em",
    letterSpacing: "-0.02em",
    textShadow: "0 2px 8px rgba(0,0,0,0.9), 0 0 2px rgba(0,0,0,1)",
  },
  fade: {
    fontWeight: 600,
    fontSize: "1em",
    textShadow: "0 1px 6px rgba(0,0,0,0.8)",
  },
  "bold-impact": {
    fontWeight: 900,
    fontSize: "1.4em",
    textTransform: "uppercase" as const,
    letterSpacing: "0.05em",
    WebkitTextStroke: "1px rgba(0,0,0,0.5)",
    textShadow: "0 3px 8px rgba(0,0,0,0.95)",
  },
  "slide-up": {
    fontWeight: 700,
    fontSize: "1.05em",
    borderLeft: "3px solid #FBBF24",
    paddingLeft: 8,
    textShadow: "0 2px 6px rgba(0,0,0,0.8)",
  },
  pop: {
    fontWeight: 900,
    fontSize: "1.2em",
    textShadow: "0 0 12px rgba(239,68,68,0.5), 0 2px 6px rgba(0,0,0,0.8)",
  },
  "kinetic-bounce": {
    fontWeight: 900,
    fontSize: "1.1em",
    letterSpacing: "-0.01em",
    textShadow: "0 2px 6px rgba(0,0,0,0.8)",
  },
  glow: {
    fontWeight: 700,
    fontSize: "1.05em",
    textShadow: "0 0 8px #fff, 0 0 16px #a78bfa, 0 0 32px #a78bfa",
    border: "1px solid rgba(167,139,250,0.3)",
  },
  "motion-card": {
    fontWeight: 700,
    fontSize: "1em",
    textShadow: "0 2px 6px rgba(0,0,0,0.6)",
  },
  typewriter: {
    fontFamily: "monospace",
    fontWeight: 600,
    fontSize: "0.95em",
    color: "#4ade80",
    textShadow: "0 0 6px rgba(74,222,128,0.3)",
    border: "1px solid rgba(74,222,128,0.2)",
  },
};

// --- Overlay renderers (matching Remotion output style) ---

const TITLE_GRADIENTS: Record<string, string> = {
  dark: "linear-gradient(155deg, #0a0a0a 0%, #1a1a2e 45%, #16213e 100%)",
  red: "linear-gradient(155deg, #1a0000 0%, #4a0011 45%, #8b0000 100%)",
  purple: "linear-gradient(155deg, #0d0015 0%, #2d004f 45%, #5c0099 100%)",
  ocean: "linear-gradient(155deg, #00090d 0%, #003344 45%, #006666 100%)",
  sunset: "linear-gradient(155deg, #1a0800 0%, #6b2100 45%, #b84100 100%)",
  neon: "linear-gradient(155deg, #05000d 0%, #1a003a 45%, #330066 100%)",
};

const TITLE_ACCENTS: Record<string, string> = {
  dark: "#FBBF24",
  red: "#FF4444",
  purple: "#A855F7",
  ocean: "#22D3EE",
  sunset: "#FB923C",
  neon: "#E879F9",
};

function renderTitleSlide(payload: Record<string, unknown>): React.ReactNode {
  const headline = str(payload.headline);
  const subheadline = str(payload.subheadline);
  const theme = str(payload.theme, "dark");
  const gradient = TITLE_GRADIENTS[theme] ?? TITLE_GRADIENTS.dark;
  const accent = TITLE_ACCENTS[theme] ?? TITLE_ACCENTS.dark;
  return (
    <div
      className="absolute inset-0 flex flex-col items-center justify-center px-[8%]"
      style={{
        background: gradient,
        animation: "titleSlideIn 0.5s cubic-bezier(0.16, 1, 0.3, 1) both",
      }}
    >
      <div
        style={{
          width: "28%",
          height: 3,
          backgroundColor: accent,
          borderRadius: 2,
          marginBottom: "3%",
          boxShadow: `0 0 12px ${accent}66`,
          animation: "lineDrawIn 0.6s 0.15s cubic-bezier(0.16, 1, 0.3, 1) both",
        }}
      />
      <div
        style={{
          color: "white",
          fontWeight: 900,
          fontSize: "1.6em",
          textAlign: "center",
          lineHeight: 1.12,
          letterSpacing: "-0.02em",
          textShadow: "0 4px 20px rgba(0,0,0,0.5)",
          animation: "textPopIn 0.5s 0.2s cubic-bezier(0.34, 1.56, 0.64, 1) both",
        }}
      >
        {headline}
      </div>
      {subheadline && (
        <div
          style={{
            color: "rgba(255,255,255,0.55)",
            fontWeight: 500,
            fontSize: "0.7em",
            textAlign: "center",
            marginTop: "2%",
            animation: "subtitleSlideUp 0.5s 0.35s cubic-bezier(0.16, 1, 0.3, 1) both",
          }}
        >
          {subheadline}
        </div>
      )}
      <div
        style={{
          width: "28%",
          height: 3,
          backgroundColor: accent,
          borderRadius: 2,
          marginTop: "3%",
          boxShadow: `0 0 12px ${accent}66`,
          animation: "lineDrawIn 0.6s 0.15s cubic-bezier(0.16, 1, 0.3, 1) both",
        }}
      />
    </div>
  );
}

function renderStatCounter(payload: Record<string, unknown>): React.ReactNode {
  const prefix = str(payload.prefix);
  const suffix = str(payload.suffix);
  const targetValue = str(payload.targetValue, "0");
  const label = str(payload.label);
  return (
    <div className="absolute bottom-[18%] left-0 right-0 flex justify-center">
      <div
        style={{
          backgroundColor: "rgba(0,0,0,0.88)",
          backdropFilter: "blur(12px)",
          borderRadius: 12,
          padding: "10px 20px",
          textAlign: "center",
          border: "1px solid rgba(251,191,36,0.15)",
          boxShadow: "0 0 20px rgba(251,191,36,0.08), 0 8px 32px rgba(0,0,0,0.5)",
        }}
      >
        <div
          style={{
            color: "#FBBF24",
            fontWeight: 900,
            fontSize: "2em",
            lineHeight: 1,
            fontVariantNumeric: "tabular-nums",
          }}
        >
          {prefix}
          {targetValue}
          {suffix}
        </div>
        <div
          style={{
            color: "rgba(255,255,255,0.5)",
            fontWeight: 600,
            fontSize: "0.55em",
            marginTop: 4,
            letterSpacing: "0.03em",
            textTransform: "uppercase",
          }}
        >
          {label}
        </div>
      </div>
    </div>
  );
}

function renderNoteCard(payload: Record<string, unknown>): React.ReactNode {
  const title = str(payload.title);
  const body = str(payload.body);
  return (
    <div className="absolute bottom-[14%] left-0 right-0 flex justify-center px-[8%]">
      <div
        style={{
          backgroundColor: "#FFFEF5",
          borderRadius: 12,
          padding: "10px 14px",
          boxShadow: "0 12px 40px rgba(0,0,0,0.3)",
          maxWidth: "80%",
          transform: "rotate(-0.8deg)",
        }}
      >
        {/* macOS dots */}
        <div
          style={{ display: "flex", alignItems: "center", gap: 4, marginBottom: 6, opacity: 0.35 }}
        >
          <div style={{ width: 6, height: 6, borderRadius: "50%", backgroundColor: "#FF605C" }} />
          <div style={{ width: 6, height: 6, borderRadius: "50%", backgroundColor: "#FFBD44" }} />
          <div style={{ width: 6, height: 6, borderRadius: "50%", backgroundColor: "#00CA4E" }} />
          <span style={{ fontSize: "0.45em", color: "#8B8B8B", fontWeight: 500, marginLeft: 2 }}>
            Notes
          </span>
        </div>
        <div style={{ color: "#1a1a1a", fontWeight: 800, fontSize: "0.85em", lineHeight: 1.2 }}>
          {title}
        </div>
        {body && (
          <div
            style={{
              color: "#4a4a4a",
              fontWeight: 400,
              fontSize: "0.6em",
              lineHeight: 1.5,
              marginTop: 3,
            }}
          >
            {body}
          </div>
        )}
      </div>
    </div>
  );
}

function renderFloatingCard(payload: Record<string, unknown>): React.ReactNode {
  const posMap: Record<string, string> = {
    "bottom-left": "bottom-[18%] left-[5%]",
    "bottom-right": "bottom-[18%] right-[5%]",
    "top-left": "top-[12%] left-[5%]",
    "top-right": "top-[12%] right-[5%]",
    "top-center": "top-[12%] left-1/2 -translate-x-1/2",
    "bottom-center": "bottom-[18%] left-1/2 -translate-x-1/2",
  };
  const pos = str(payload.position, "bottom-left");
  const text = str(payload.text);
  return (
    <div className={`absolute ${posMap[pos] ?? posMap["bottom-left"]}`}>
      <div
        style={{
          backgroundColor: "rgba(255,255,255,0.95)",
          backdropFilter: "blur(12px)",
          borderRadius: 20,
          padding: "6px 14px",
          boxShadow: "0 6px 24px rgba(0,0,0,0.25)",
          maxWidth: 180,
        }}
      >
        <div
          style={{
            color: "#1a1a1a",
            fontWeight: 700,
            fontSize: "0.7em",
            lineHeight: 1.3,
            textAlign: "center",
          }}
        >
          {text}
        </div>
      </div>
    </div>
  );
}

function renderCommentBox(payload: Record<string, unknown>): React.ReactNode {
  const username = str(payload.username, "user");
  const text = str(payload.text);
  const initial = username.charAt(0).toUpperCase();
  return (
    <div className="absolute bottom-[14%] left-0 right-0 flex justify-center px-[5%]">
      <div
        style={{
          backgroundColor: "rgba(38,38,38,0.95)",
          backdropFilter: "blur(8px)",
          borderRadius: 14,
          padding: "8px 12px",
          maxWidth: "85%",
          border: "1px solid rgba(255,255,255,0.08)",
        }}
      >
        <div style={{ display: "flex", alignItems: "center", gap: 6, marginBottom: 4 }}>
          <div
            style={{
              width: 20,
              height: 20,
              borderRadius: "50%",
              background: "linear-gradient(135deg, #F58529, #DD2A7B, #8134AF)",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              fontSize: "0.45em",
              fontWeight: 700,
              color: "white",
            }}
          >
            {initial}
          </div>
          <span style={{ fontSize: "0.65em", fontWeight: 700, color: "white" }}>{username}</span>
          <span
            style={{
              width: 10,
              height: 10,
              borderRadius: "50%",
              backgroundColor: "#3897f0",
              display: "inline-flex",
              alignItems: "center",
              justifyContent: "center",
              fontSize: "0.35em",
              color: "white",
              fontWeight: 900,
            }}
          >
            ✓
          </span>
        </div>
        <div
          style={{
            fontSize: "0.65em",
            color: "rgba(255,255,255,0.9)",
            lineHeight: 1.4,
            paddingLeft: 26,
          }}
        >
          {text}
        </div>
      </div>
    </div>
  );
}

function renderRetentionChart(payload: Record<string, unknown>): React.ReactNode {
  const chartLabel = str(payload.chartLabel);
  const rawPoints = (payload.dataPoints ?? []) as Array<Record<string, unknown>>;
  if (rawPoints.length === 0) return null;
  const values = rawPoints.map((p) => Number(p.value) || 0);
  const labels = rawPoints.map((p) => str(p.label));
  const maxVal = Math.max(...values, 1);

  return (
    <div className="absolute bottom-[14%] left-0 right-0 flex justify-center px-[8%]">
      <div
        style={{
          backgroundColor: "rgba(15,15,20,0.92)",
          backdropFilter: "blur(12px)",
          borderRadius: 10,
          padding: "8px 10px",
          width: "80%",
          border: "1px solid rgba(255,255,255,0.06)",
          boxShadow: "0 12px 40px rgba(0,0,0,0.4)",
        }}
      >
        <div
          style={{
            fontSize: "0.6em",
            fontWeight: 700,
            color: "white",
            marginBottom: 6,
            display: "flex",
            alignItems: "center",
            gap: 4,
          }}
        >
          <span style={{ opacity: 0.6 }}>📈</span> {chartLabel}
        </div>
        {/* SVG chart */}
        <svg viewBox="0 0 200 60" style={{ width: "100%", height: "auto" }}>
          <defs>
            <linearGradient id="prevChartGrad" x1="0" y1="0" x2="0" y2="1">
              <stop offset="0%" stopColor="#22d3ee" stopOpacity={0.25} />
              <stop offset="100%" stopColor="#22d3ee" stopOpacity={0.02} />
            </linearGradient>
          </defs>
          {(() => {
            const pts = values.map((v, i) => ({
              x: 10 + (i / Math.max(values.length - 1, 1)) * 180,
              y: 5 + (1 - v / maxVal) * 40,
            }));
            const linePath = pts.map((p, i) => `${i === 0 ? "M" : "L"} ${p.x} ${p.y}`).join(" ");
            const areaPath = `${linePath} L ${pts[pts.length - 1].x} 45 L ${pts[0].x} 45 Z`;
            const lastPt = pts[pts.length - 1];
            return (
              <>
                <path d={areaPath} fill="url(#prevChartGrad)" />
                <path
                  d={linePath}
                  fill="none"
                  stroke="#22d3ee"
                  strokeWidth={1.5}
                  strokeLinecap="round"
                  opacity={0.3}
                />
                <path
                  d={linePath}
                  fill="none"
                  stroke="#22d3ee"
                  strokeWidth={1}
                  strokeLinecap="round"
                />
                <circle cx={lastPt.x} cy={lastPt.y} r={2.5} fill="#22d3ee" />
                {labels.map((label, i) => (
                  <text
                    key={i}
                    x={pts[i].x}
                    y={55}
                    textAnchor="middle"
                    fontSize={5}
                    fill="rgba(255,255,255,0.35)"
                  >
                    {label}
                  </text>
                ))}
              </>
            );
          })()}
        </svg>
      </div>
    </div>
  );
}

function renderComparisonRows(payload: Record<string, unknown>): React.ReactNode {
  const rows = (payload.rows as Array<Record<string, string>>) ?? [];
  const title = str(payload.title);
  return (
    <div
      className="absolute inset-0 flex flex-col items-center justify-center px-[5%]"
      style={{ background: "linear-gradient(175deg, #0a0a0f 0%, #1a1a2e 50%, #16213e 100%)" }}
    >
      {title && (
        <div
          style={{
            color: "white",
            fontWeight: 800,
            fontSize: "0.85em",
            marginBottom: 8,
            textAlign: "center",
          }}
        >
          {title}
        </div>
      )}
      <div style={{ display: "flex", flexDirection: "column", gap: 4, width: "88%" }}>
        {rows.map((row, i) => (
          <div
            key={i}
            style={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              backgroundColor: "rgba(255,255,255,0.06)",
              backdropFilter: "blur(8px)",
              borderRadius: 8,
              padding: "5px 10px",
              border: "1px solid rgba(255,255,255,0.08)",
            }}
          >
            <div style={{ textAlign: "center", flex: 1 }}>
              <div
                style={{
                  fontSize: "0.4em",
                  color: "rgba(255,255,255,0.4)",
                  fontWeight: 500,
                  textTransform: "uppercase",
                  letterSpacing: "0.05em",
                }}
              >
                {row.leftLabel}
              </div>
              <div style={{ fontSize: "0.7em", fontWeight: 900, color: "white" }}>
                {row.leftValue}
              </div>
            </div>
            <div
              style={{
                fontSize: "0.55em",
                fontWeight: 700,
                color: "#FBBF24",
                textAlign: "center",
                flex: 1,
              }}
            >
              {row.label}
            </div>
            <div style={{ textAlign: "center", flex: 1 }}>
              <div
                style={{
                  fontSize: "0.4em",
                  color: "rgba(255,255,255,0.4)",
                  fontWeight: 500,
                  textTransform: "uppercase",
                  letterSpacing: "0.05em",
                }}
              >
                {row.rightLabel}
              </div>
              <div style={{ fontSize: "0.7em", fontWeight: 900, color: "#22d3ee" }}>
                {row.rightValue}
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

function renderCirclePortrait(payload: Record<string, unknown>): React.ReactNode {
  const isDark = str(payload.theme, "dark") !== "light";
  const text = str(payload.text);
  const subtext = str(payload.subtext);
  return (
    <div
      className="absolute inset-0 flex flex-col items-center justify-center px-[6%]"
      style={{ backgroundColor: isDark ? "#0a0a0a" : "#f2f2f7" }}
    >
      {/* Portrait ring */}
      <div
        style={{
          width: "35%",
          aspectRatio: "1",
          borderRadius: "50%",
          background: isDark
            ? "conic-gradient(from 0deg, #FBBF24, #F59E0B, #FBBF24, transparent, #FBBF24)"
            : "conic-gradient(from 0deg, #e5e7eb, #d1d5db, #e5e7eb)",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          marginBottom: "4%",
          boxShadow: isDark ? "0 0 30px rgba(251,191,36,0.2)" : "0 8px 40px rgba(0,0,0,0.15)",
        }}
      >
        <div
          style={{
            width: "92%",
            height: "92%",
            borderRadius: "50%",
            backgroundColor: isDark ? "#1a1a1a" : "#e5e5e5",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            fontSize: "2em",
          }}
        >
          👤
        </div>
      </div>
      <div
        style={{
          fontWeight: 900,
          fontSize: "1em",
          color: isDark ? "white" : "#1a1a1a",
          textAlign: "center",
          lineHeight: 1.15,
          letterSpacing: "-0.02em",
        }}
      >
        {text}
      </div>
      {subtext && (
        <div
          style={{
            fontWeight: 500,
            fontSize: "0.5em",
            color: isDark ? "rgba(255,255,255,0.5)" : "rgba(0,0,0,0.45)",
            marginTop: 4,
          }}
        >
          {subtext}
        </div>
      )}
    </div>
  );
}

function renderProgressBar(payload: Record<string, unknown>): React.ReactNode {
  const label = str(payload.label);
  const percentage = Number(payload.percentage) || 0;
  const sublabel = str(payload.sublabel);
  return (
    <div className="absolute bottom-[18%] left-0 right-0 flex justify-center px-[8%]">
      <div
        style={{
          backgroundColor: "rgba(10,10,15,0.92)",
          backdropFilter: "blur(12px)",
          borderRadius: 10,
          padding: "8px 12px",
          width: "78%",
          border: "1px solid rgba(255,255,255,0.06)",
          boxShadow: "0 12px 40px rgba(0,0,0,0.4)",
        }}
      >
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "baseline",
            marginBottom: 4,
          }}
        >
          <span style={{ fontSize: "0.65em", fontWeight: 800, color: "white" }}>{label}</span>
          <span
            style={{
              fontSize: "0.75em",
              fontWeight: 900,
              color: "#FBBF24",
              fontVariantNumeric: "tabular-nums",
            }}
          >
            {Math.round(percentage)}%
          </span>
        </div>
        <div
          style={{
            width: "100%",
            height: 6,
            backgroundColor: "rgba(255,255,255,0.08)",
            borderRadius: 3,
            overflow: "hidden",
          }}
        >
          <div
            style={{
              width: `${percentage}%`,
              height: "100%",
              background: "linear-gradient(90deg, #F59E0B, #FBBF24, #FDE68A)",
              borderRadius: 3,
              boxShadow: "0 0 8px rgba(251,191,36,0.4)",
            }}
          />
        </div>
        {sublabel && (
          <div
            style={{
              marginTop: 3,
              fontSize: "0.45em",
              fontWeight: 500,
              color: "rgba(255,255,255,0.4)",
            }}
          >
            {sublabel}
          </div>
        )}
      </div>
    </div>
  );
}

function renderBulletList(payload: Record<string, unknown>): React.ReactNode {
  const title = str(payload.title);
  const items = (payload.items as string[]) ?? [];
  const emojis = ["💡", "🔥", "✅", "⚡", "🎯", "📌"];
  return (
    <div className="absolute bottom-[14%] left-0 right-0 flex justify-center px-[6%]">
      <div
        style={{
          backgroundColor: "rgba(10,10,15,0.92)",
          backdropFilter: "blur(12px)",
          borderRadius: 10,
          padding: "8px 12px",
          width: "82%",
          border: "1px solid rgba(255,255,255,0.06)",
          boxShadow: "0 16px 48px rgba(0,0,0,0.5)",
        }}
      >
        <div
          style={{
            fontSize: "0.75em",
            fontWeight: 900,
            color: "white",
            marginBottom: 4,
            letterSpacing: "-0.02em",
          }}
        >
          {title}
        </div>
        <div
          style={{
            width: "20%",
            height: 2,
            backgroundColor: "#FBBF24",
            borderRadius: 1,
            marginBottom: 5,
            boxShadow: "0 0 6px rgba(251,191,36,0.3)",
          }}
        />
        {items.map((item, i) => (
          <div
            key={i}
            style={{
              display: "flex",
              alignItems: "flex-start",
              gap: 4,
              marginBottom: 2,
            }}
          >
            <span style={{ fontSize: "0.5em", flexShrink: 0 }}>{emojis[i % emojis.length]}</span>
            <span
              style={{
                fontSize: "0.55em",
                fontWeight: 600,
                color: "rgba(255,255,255,0.88)",
                lineHeight: 1.4,
              }}
            >
              {item}
            </span>
          </div>
        ))}
      </div>
    </div>
  );
}

const GRADIENT_PRESETS: Record<string, { bg: string; accent: string }> = {
  "dark-purple": {
    bg: "linear-gradient(160deg, #0a0a1a 0%, #1a0533 50%, #2d1b69 100%)",
    accent: "#a78bfa",
  },
  "dark-red": {
    bg: "linear-gradient(160deg, #0a0000 0%, #2d0000 50%, #7f1d1d 100%)",
    accent: "#ef4444",
  },
  "dark-ocean": {
    bg: "linear-gradient(160deg, #0a0f1a 0%, #0c2340 50%, #164e63 100%)",
    accent: "#22d3ee",
  },
  "dark-green": {
    bg: "linear-gradient(160deg, #010a03 0%, #052e16 50%, #166534 100%)",
    accent: "#4ade80",
  },
  "dark-gold": {
    bg: "linear-gradient(160deg, #0a0800 0%, #292100 50%, #713f12 100%)",
    accent: "#fbbf24",
  },
  charcoal: {
    bg: "linear-gradient(160deg, #09090b 0%, #18181b 50%, #27272a 100%)",
    accent: "#e4e4e7",
  },
};

function renderGradientReveal(payload: Record<string, unknown>): React.ReactNode {
  const headline = str(payload.headline);
  const subheadline = str(payload.subheadline);
  const body = str(payload.body);
  const preset = str(payload.preset, "dark-purple");
  const { bg, accent } = GRADIENT_PRESETS[preset] ?? GRADIENT_PRESETS["dark-purple"];

  return (
    <div
      className="absolute inset-0 flex flex-col items-center justify-center px-[8%]"
      style={{
        background: bg,
        animation: "gradientRevealIn 0.45s cubic-bezier(0.16, 1, 0.3, 1) both",
      }}
    >
      {/* Accent line */}
      <div
        style={{
          width: "30%",
          height: 3,
          backgroundColor: accent,
          borderRadius: 2,
          marginBottom: "4%",
          boxShadow: `0 0 16px ${accent}88`,
          animation: "lineDrawIn 0.5s 0.1s cubic-bezier(0.16, 1, 0.3, 1) both",
        }}
      />
      {/* Headline */}
      <div
        style={{
          color: "white",
          fontWeight: 900,
          fontSize: "1.55em",
          textAlign: "center",
          lineHeight: 1.1,
          letterSpacing: "-0.025em",
          textShadow: `0 4px 24px rgba(0,0,0,0.4), 0 0 40px ${accent}33`,
          animation: "textPopIn 0.45s 0.15s cubic-bezier(0.34, 1.56, 0.64, 1) both",
        }}
      >
        {headline}
      </div>
      {/* Subheadline */}
      {subheadline && (
        <div
          style={{
            color: accent,
            fontWeight: 700,
            fontSize: "0.72em",
            textAlign: "center",
            marginTop: "2.5%",
            letterSpacing: "0.03em",
            textTransform: "uppercase",
            animation: "subtitleSlideUp 0.45s 0.28s cubic-bezier(0.16, 1, 0.3, 1) both",
          }}
        >
          {subheadline}
        </div>
      )}
      {/* Body */}
      {body && (
        <div
          style={{
            color: "rgba(255,255,255,0.5)",
            fontWeight: 400,
            fontSize: "0.58em",
            textAlign: "center",
            marginTop: "3%",
            lineHeight: 1.6,
            maxWidth: "80%",
            animation: "subtitleSlideUp 0.45s 0.36s cubic-bezier(0.16, 1, 0.3, 1) both",
          }}
        >
          {body}
        </div>
      )}
      {/* Bottom accent line */}
      <div
        style={{
          width: "30%",
          height: 3,
          backgroundColor: accent,
          borderRadius: 2,
          marginTop: "4%",
          boxShadow: `0 0 16px ${accent}88`,
          animation: "lineDrawIn 0.5s 0.1s cubic-bezier(0.16, 1, 0.3, 1) both",
        }}
      />
    </div>
  );
}

const OVERLAY_RENDERERS: Record<string, (payload: Record<string, unknown>) => React.ReactNode> = {
  "gradient-reveal": renderGradientReveal,
  "title-slide": renderTitleSlide,
  "stat-counter": renderStatCounter,
  "note-card": renderNoteCard,
  "floating-card": renderFloatingCard,
  "comment-box": renderCommentBox,
  "retention-chart": renderRetentionChart,
  "comparison-rows": renderComparisonRows,
  "circle-portrait": renderCirclePortrait,
  "progress-bar": renderProgressBar,
  "bullet-list": renderBulletList,
};

// --- CSS keyframes for overlay animations ---

const OVERLAY_KEYFRAMES = `
@keyframes gradientRevealIn {
  0% { opacity: 0; clip-path: inset(100% 0 0 0); }
  100% { opacity: 1; clip-path: inset(0% 0 0 0); }
}
@keyframes titleSlideIn {
  0% { opacity: 0; transform: scale(1.5); }
  100% { opacity: 1; transform: scale(1); }
}
@keyframes lineDrawIn {
  0% { width: 0; opacity: 0; }
  100% { opacity: 1; }
}
@keyframes textPopIn {
  0% { opacity: 0; transform: scale(0); }
  60% { transform: scale(1.12); }
  100% { opacity: 1; transform: scale(1); }
}
@keyframes subtitleSlideUp {
  0% { opacity: 0; transform: translateY(20px); }
  100% { opacity: 1; transform: translateY(0); }
}
@keyframes overlayFadeSlideIn {
  0% { opacity: 0; transform: translateY(12px) scale(0.95); }
  100% { opacity: 1; transform: translateY(0) scale(1); }
}
`;

// --- Main component ---

export default function VideoPreviewOverlay({
  currentTime,
  subtitles,
  overlays,
  captionStyle,
  captionCustom,
  onOverlayClick,
  onCustomOverlayOffsetChange,
  showSubtitles = true,
}: VideoPreviewOverlayProps) {
  const [dragPreviewOffsetY, setDragPreviewOffsetY] = useState<number | null>(null);
  const [dragOverlayIndex, setDragOverlayIndex] = useState<number | null>(null);

  const dragStateRef = useRef<{
    overlayIndex: number;
    startY: number;
    startOffsetY: number;
    currentOffsetY: number;
  } | null>(null);

  const activeSub = useMemo(
    () => findActiveSubtitle(subtitles, currentTime),
    [subtitles, currentTime]
  );

  const activeOverlays = useMemo(
    () => findActiveOverlays(overlays, currentTime),
    [overlays, currentTime]
  );

  // Get original indices for click handler
  const activeOverlayIndices = useMemo(
    () =>
      overlays.reduce<number[]>((acc, o, i) => {
        if (currentTime >= o.startTime && currentTime < o.endTime) acc.push(i);
        return acc;
      }, []),
    [overlays, currentTime]
  );

  // Hook przed early-return (rules-of-hooks; review #7) — rejestruje drag-listenery
  // custom-overlay; no-op gdy brak onCustomOverlayOffsetChange.
  useEffect(() => {
    if (!onCustomOverlayOffsetChange) return;

    const handlePointerMove = (event: PointerEvent) => {
      const dragState = dragStateRef.current;
      if (!dragState) return;

      const deltaY = event.clientY - dragState.startY;
      const nextOffsetY = Math.round(dragState.startOffsetY + deltaY);
      const clampedOffsetY = Math.max(-600, Math.min(600, nextOffsetY));
      if (!Number.isFinite(clampedOffsetY)) return;

      dragState.currentOffsetY = clampedOffsetY;
      setDragOverlayIndex(dragState.overlayIndex);
      setDragPreviewOffsetY(clampedOffsetY);
    };

    const handlePointerUp = () => {
      const dragState = dragStateRef.current;
      if (dragState && Number.isFinite(dragState.currentOffsetY)) {
        onCustomOverlayOffsetChange(dragState.overlayIndex, dragState.currentOffsetY);
      }
      dragStateRef.current = null;
      setDragOverlayIndex(null);
      setDragPreviewOffsetY(null);
    };

    window.addEventListener("pointermove", handlePointerMove);
    window.addEventListener("pointerup", handlePointerUp);

    return () => {
      window.removeEventListener("pointermove", handlePointerMove);
      window.removeEventListener("pointerup", handlePointerUp);
    };
  }, [onCustomOverlayOffsetChange]);

  const hasAnything = activeSub || activeOverlays.length > 0;
  if (!hasAnything) return null;

  // Hide captions during fullscreen overlays, move to top during bottom-zone overlays
  const hasFullscreen = activeOverlays.some((o) => FULLSCREEN_OVERLAY_TYPES.has(o.type));
  const hasCustomCodeOverlay = activeOverlays.some((o) => o.type === "custom-code");
  const hasBottomOverlay = activeOverlays.some((o) => {
    if (BOTTOM_ZONE_OVERLAY_TYPES.has(o.type)) return true;
    if (o.type === "floating-card") {
      const pos = String(o.payload.position ?? "bottom-left");
      return pos.startsWith("bottom");
    }
    return false;
  });
  const captionConfig = CAPTION_CONFIGS[captionStyle] ?? CAPTION_CONFIGS.karaoke;
  const captionPosition = hasBottomOverlay ? "top" : "bottom";

  return (
    <div className="absolute inset-0 pointer-events-none overflow-hidden rounded-lg">
      {/* Inject keyframe animations */}
      <style>{OVERLAY_KEYFRAMES}</style>

      {/* Motion graphics overlays */}
      {activeOverlays.map((overlay, i) => {
        const isCustomCode = overlay.type === "custom-code";
        const renderer = isCustomCode ? null : OVERLAY_RENDERERS[overlay.type];
        if (!renderer && !isCustomCode) return null;
        const isFullscreen = FULLSCREEN_OVERLAY_TYPES.has(overlay.type);
        const originalOverlayIndex = activeOverlayIndices[i];
        const payloadOffset = Number(overlay.payload.previewOffsetY ?? 0);
        const baseOffsetY = Number.isFinite(payloadOffset) ? payloadOffset : 0;
        const currentOffsetY =
          dragOverlayIndex === originalOverlayIndex && dragPreviewOffsetY !== null
            ? dragPreviewOffsetY
            : baseOffsetY;

        const overlayContent = isCustomCode ? (
          <DynamicOverlayPreview
            compiled={String(overlay.payload.compiled ?? "")}
            currentFrame={Math.max(0, Math.round((currentTime - overlay.startTime) * 30))}
            fps={30}
            width={1080}
            height={1920}
            durationInFrames={Math.round((overlay.endTime - overlay.startTime) * 30)}
          />
        ) : (
          renderer!(overlay.payload)
        );

        return (
          <div
            key={`overlay-${overlay.type}-${i}`}
            className={
              onOverlayClick ? "pointer-events-auto cursor-pointer" : "pointer-events-none"
            }
            onClick={
              onOverlayClick
                ? (e) => {
                    e.stopPropagation();
                    onOverlayClick(originalOverlayIndex);
                  }
                : undefined
            }
            onPointerDown={
              isCustomCode && onCustomOverlayOffsetChange
                ? (event) => {
                    event.stopPropagation();
                    if (typeof originalOverlayIndex !== "number") return;
                    dragStateRef.current = {
                      overlayIndex: originalOverlayIndex,
                      startY: event.clientY,
                      startOffsetY: currentOffsetY,
                      currentOffsetY,
                    };
                    setDragOverlayIndex(originalOverlayIndex);
                    setDragPreviewOffsetY(currentOffsetY);
                  }
                : undefined
            }
            style={
              isFullscreen || isCustomCode
                ? {
                    position: "relative",
                    width: "100%",
                    height: "100%",
                    transform: isCustomCode ? `translateY(${currentOffsetY}px)` : undefined,
                  }
                : { animation: "overlayFadeSlideIn 0.35s cubic-bezier(0.16, 1, 0.3, 1) both" }
            }
            title={onOverlayClick ? "Kliknij aby edytować" : undefined}
          >
            {overlayContent}
          </div>
        );
      })}

      {/* Subtitle — hidden during fullscreen overlays, moved to top during bottom-zone overlays */}
      {activeSub &&
        !hasFullscreen &&
        showSubtitles &&
        !hasCustomCodeOverlay &&
        renderActiveSubtitle({
          sub: activeSub,
          currentTime,
          captionStyle,
          captionCustom,
          captionConfig,
          captionPosition,
        })}
    </div>
  );
}

// --- Subtitle preview renderers (parity with Remotion compositions) ---

interface SubtitleRenderArgs {
  sub: Subtitle;
  currentTime: number;
  captionStyle: string;
  captionCustom?: CaptionCustomStyle;
  captionConfig: React.CSSProperties;
  captionPosition: "top" | "bottom";
}

function applyTransform(s: string, transform?: "none" | "uppercase" | "lowercase"): string {
  if (transform === "uppercase") return s.toUpperCase();
  if (transform === "lowercase") return s.toLowerCase();
  return s;
}

function getDisplayWords(sub: Subtitle): Array<{ word: string; start: number; end: number }> {
  if (sub.words && sub.words.length > 0) return sub.words;
  const parts = sub.text.split(/\s+/).filter(Boolean);
  const dur = Math.max(0.001, sub.endTime - sub.startTime);
  const seg = dur / Math.max(parts.length, 1);
  return parts.map((w, i) => ({
    word: w,
    start: sub.startTime + i * seg,
    end: sub.startTime + (i + 1) * seg,
  }));
}

function renderActiveSubtitle(args: SubtitleRenderArgs): React.ReactNode {
  const { captionStyle } = args;
  if (captionStyle === "karaoke") return renderKaraokePreview(args);
  if (captionStyle === "word-by-word") return renderWordByWordPreview(args);
  return renderPlainPreview(args);
}

const KARAOKE_ACTIVE = "#FBBF24";
const KARAOKE_ACTIVE_BG = "rgba(251,191,36,0.25)";
const KARAOKE_INACTIVE = "rgba(255,255,255,0.55)";

function renderKaraokePreview({
  sub,
  currentTime,
  captionCustom,
  captionPosition,
}: SubtitleRenderArgs): React.ReactNode {
  const words = getDisplayWords(sub);
  const transform = captionCustom?.textTransform;
  const fontFamily = captionCustom?.fontFamily ?? "Arial, Helvetica, sans-serif";
  const fontWeight = captionCustom?.fontWeight ?? 900;
  const activeColor = captionCustom?.textColor ?? KARAOKE_ACTIVE;
  const inactiveColor = KARAOKE_INACTIVE;
  const shadowOn = captionCustom?.dropShadow !== false;
  const shadowBlur = captionCustom?.shadowBlur ?? 10;
  const shadowColor = captionCustom?.shadowColor ?? "rgba(0,0,0,0.95)";
  const textShadow = shadowOn
    ? `0 2px ${shadowBlur}px ${shadowColor}, 0 0 30px rgba(0,0,0,0.6)`
    : "none";

  return (
    <div
      className={`absolute ${captionPosition === "top" ? "top-[12%]" : "bottom-[16%]"} left-0 right-0 flex justify-center px-[6%]`}
    >
      <div
        style={{
          display: "flex",
          flexWrap: "wrap",
          justifyContent: "center",
          alignItems: "center",
          gap: "4px 6px",
          maxWidth: "88%",
          lineHeight: 1.35,
        }}
      >
        {words.map((w, i) => {
          const isActive = currentTime >= w.start && currentTime < w.end;
          const isPast = currentTime >= w.end;
          const isHighlighted = isActive || isPast;
          const color = isHighlighted ? activeColor : inactiveColor;
          const bg = isActive ? KARAOKE_ACTIVE_BG : "transparent";
          return (
            <span
              key={i}
              style={{
                fontSize: captionCustom?.fontSize ?? "1.15em",
                fontFamily,
                fontWeight,
                color,
                textShadow,
                backgroundColor: bg,
                borderRadius: 4,
                padding: isActive ? "1px 4px" : "0",
                transform: isActive ? "scale(1.08)" : "scale(1)",
                transition: "transform 80ms ease-out, background-color 80ms ease-out",
                display: "inline-block",
                letterSpacing: "-0.02em",
              }}
            >
              {applyTransform(w.word, transform)}
            </span>
          );
        })}
      </div>
    </div>
  );
}

function renderWordByWordPreview({
  sub,
  currentTime,
  captionCustom,
  captionPosition,
}: SubtitleRenderArgs): React.ReactNode {
  const words = getDisplayWords(sub);
  const activeIdx = words.findIndex((w) => currentTime >= w.start && currentTime < w.end);
  const lastIdx = words.length - 1;
  const idx =
    activeIdx === -1 ? (currentTime >= (words[lastIdx]?.end ?? 0) ? lastIdx : 0) : activeIdx;
  const active = words[idx];
  if (!active) return null;

  const transform = captionCustom?.textTransform ?? "uppercase";
  const fontFamily = captionCustom?.fontFamily ?? "Arial Black, Helvetica, sans-serif";
  const fontWeight = captionCustom?.fontWeight ?? 900;
  const color = captionCustom?.textColor ?? "#FFFFFF";
  const bgColor = captionCustom?.bgColor ?? "rgba(0,0,0,0)";
  const shadowOn = captionCustom?.dropShadow !== false;
  const shadowBlur = captionCustom?.shadowBlur ?? 16;
  const shadowColor = captionCustom?.shadowColor ?? "rgba(0,0,0,0.9)";
  const textShadow = shadowOn
    ? `0 4px ${shadowBlur}px ${shadowColor}, 0 0 40px rgba(0,0,0,0.7)`
    : "none";

  return (
    <div
      className={`absolute inset-0 flex justify-center ${captionPosition === "top" ? "items-start pt-[20%]" : "items-center"}`}
    >
      <div
        key={`wbw-${idx}`}
        style={{
          backgroundColor: bgColor,
          borderRadius: 8,
          padding: bgColor !== "rgba(0,0,0,0)" ? "6px 14px" : 0,
          maxWidth: "92%",
          textAlign: "center",
          animation: "wbwPopIn 180ms cubic-bezier(0.34, 1.56, 0.64, 1) both",
        }}
      >
        <span
          style={{
            fontSize: captionCustom?.fontSize ?? "2.2em",
            fontFamily,
            fontWeight,
            color,
            textShadow,
            letterSpacing: "-0.02em",
            lineHeight: 1.05,
            display: "inline-block",
          }}
        >
          {applyTransform(active.word, transform)}
        </span>
      </div>
      <style>{`@keyframes wbwPopIn { 0% { opacity: 0; transform: scale(0.6); } 100% { opacity: 1; transform: scale(1); } }`}</style>
    </div>
  );
}

function renderPlainPreview({
  sub,
  captionCustom,
  captionConfig,
  captionPosition,
}: SubtitleRenderArgs): React.ReactNode {
  return (
    <div
      className={`absolute ${captionPosition === "top" ? "top-[12%]" : "bottom-[16%]"} left-0 right-0 flex justify-center px-[6%]`}
    >
      <div
        style={{
          padding: "4px 12px",
          borderRadius: 8,
          backgroundColor: captionCustom?.bgColor ?? "rgba(0,0,0,0.7)",
          backdropFilter: "blur(8px)",
          maxWidth: "90%",
          textAlign: "center",
          color: captionCustom?.textColor ?? "white",
          fontFamily: captionCustom?.fontFamily ?? "Arial, Helvetica, sans-serif",
          fontSize: captionCustom?.fontSize,
          fontWeight: captionCustom?.fontWeight,
          textTransform: (captionCustom?.textTransform ??
            "none") as React.CSSProperties["textTransform"],
          textShadow:
            captionCustom?.dropShadow === false
              ? "none"
              : captionCustom?.shadowBlur != null
                ? `0 2px ${captionCustom.shadowBlur}px ${captionCustom.shadowColor ?? "rgba(0,0,0,0.9)"}`
                : undefined,
          ...captionConfig,
          ...(captionCustom
            ? {
                backgroundColor: captionCustom.bgColor,
                color: captionCustom.textColor,
                fontFamily: captionCustom.fontFamily,
                fontSize: captionCustom.fontSize,
                fontWeight: captionCustom.fontWeight,
                textTransform: captionCustom.textTransform as React.CSSProperties["textTransform"],
              }
            : {}),
        }}
      >
        {applyTransform(sub.text, captionCustom?.textTransform)}
      </div>
    </div>
  );
}
